import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    collection,
    getDocs,
    deleteDoc,
    addDoc,
    query,
    limit,
    orderBy,
    startAfter,
    DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAuth } from 'firebase/auth';

export interface ChildInfo {
    id: string;
    name: string;
    birthdate?: string;
    age?: number;
    avatar: string;
    gender: 'male' | 'female';
    color?: string;
}

export interface UserSettings {
    children?: ChildInfo[];
    lessonAlertThreshold?: number;
    expiryAlertDays?: number;
    notifications?: {
        lessonLow?: boolean;
        paymentDue?: boolean;
        dailyReminder?: boolean;
    };
    language?: 'zh-TW' | 'en' | 'ja';
}

export interface ScheduleSlot {
    weekday: number;
    startTime: string;
    endTime: string;
}

/** 課程文件 - 儲存於 users/{uid}/children/{childId}/courses/{courseId} */
export interface CourseDoc {
    id?: string;
    /** 所屬孩子的 ID，取自 path 或回填方便前端使用 */
    childId: string;
    name: string;
    type: string;
    institution: string;
    teacher: string;
    schedule: ScheduleSlot[];
    startDate?: string;
    endDate?: string;
    location: string;
    totalLessons: number;
    usedLessons: number;
    courseFee?: number;
    /** 繳費方式：僅當課程為「依結束日期」時有效。全繳=一次繳清，月繳=按月繳費，堂繳=按當月預估堂數×每堂金額 */
    paymentMode?: 'full' | 'monthly' | 'perClass';
    /** 月繳時：每月應繳金額 */
    monthlyFee?: number;
    /** 堂繳時：每堂金額 */
    perClassFee?: number;
    /** 月繳時：每月幾號前繳費 (1-28) */
    dueDayOfMonth?: number;
    /** 月繳時：到期前幾天列入提醒 */
    reminderDaysBefore?: number;
    paused?: boolean;
    /** 最後暫停日期 (YYYY-MM-DD)，暫停當天不計入顯示區間 */
    lastPausedAt?: string;
    /** 請假是否扣堂數（舊資料未設定時以 true 視之，新建立課程 UI 預設為不扣堂） */
    leaveDeductsLesson?: boolean;
    status: 'active' | 'low' | 'expired' | 'paused' | 'done';
    alertThreshold: number;
    phone: string;
    /** @deprecated 不再儲存，顯示一律依 type 用 getCourseColor(course) */
    color?: string;
    payments: Array<{
        id: string;
        date: string;
        amount: number;
        note?: string;
        /** 月繳課程：此筆繳費所屬月份 (YYYY-MM) */
        month?: string;
        /** 繳費憑證圖片網址 */
        receiptUrl?: string;
    }>;
    /** 退款紀錄（僅做財務紀錄，總和不可超過繳費總和） */
    refunds?: Array<{
        id: string;
        date: string;
        amount: number;
        note?: string;
    }>;
    records: Array<{
        id: string;
        date: string;
        type: string;
        note?: string;
        startTime?: string;
        endTime?: string;
    }>;
    /** 未來堂課的一次性改期/改時間（仍維持為「未來課程」顯示，不視為補課/上課記錄） */
    rescheduledLessons?: Array<{
        fromDate: string;
        fromStartTime: string;
        fromEndTime: string;
        toDate: string;
        toStartTime: string;
        toEndTime: string;
    }>;
    /** 安插的單堂課（顯示於未來課程；上課日打卡後寫入 records 並自動註記補課） */
    insertedLessons?: Array<{
        id: string;
        date: string;
        startTime: string;
        endTime: string;
        note?: string;
    }>;
    /** 建立時間（ISO 字串），用於排序；舊資料可能無此欄位 */
    createdAt?: string;
}

export class Database {
    /** 快取 userSettings/settings，避免同一啟動流程重複讀取 */
    private static userSettingsCache: Promise<UserSettings | null> | null = null;

    private static getUid(): string {
        const user = getAuth().currentUser;
        if (!user) throw new Error('用戶未登入');
        return user.uid;
    }

    private static docRef(uid: string, ...pathSegments: string[]) {
        return doc(db, 'users', uid, ...pathSegments);
    }

    static async getDoc<T>(collectionName: string, docId: string): Promise<T | null> {
        const uid = this.getUid();
        const docRef = this.docRef(uid, collectionName, docId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
            return { ...snap.data(), id: snap.id } as T;
        }
        return null;
    }

    /** 取得使用者設定 userSettings/settings，帶有簡單快取以避免重複讀取 */
    static async getUserSettings(): Promise<UserSettings | null> {
        if (!this.userSettingsCache) {
            this.userSettingsCache = this.getDoc<UserSettings>('userSettings', 'settings')
                .catch((err) => {
                    // 發生錯誤時清除快取，下次可重試
                    this.userSettingsCache = null;
                    throw err;
                });
        }
        return this.userSettingsCache;
    }

    /** 在更新 userSettings 後呼叫，讓下次讀取重新打 Firestore */
    static clearUserSettingsCache() {
        this.userSettingsCache = null;
    }

    static async updateDoc(collectionName: string, docId: string, data: Record<string, any>): Promise<void> {
        const uid = this.getUid();
        const docRef = this.docRef(uid, collectionName, docId);
        try {
            await updateDoc(docRef, data);
        } catch (e: any) {
            if (e?.code === 'not-found') {
                await setDoc(docRef, data, { merge: true });
            } else {
                throw e;
            }
        }
    }

    static async setDoc(collectionName: string, docId: string, data: Record<string, any>): Promise<void> {
        const uid = this.getUid();
        const docRef = this.docRef(uid, collectionName, docId);
        await setDoc(docRef, data, { merge: true });
    }

    /** 取得單一課程，路徑 users/{uid}/children/{childId}/courses/{courseId} */
    static async getCourse(childId: string, courseId: string): Promise<CourseDoc | null> {
        const uid = this.getUid();
        const docRef = this.docRef(uid, 'children', childId, 'courses', courseId);
        const snap = await getDoc(docRef);
        if (snap.exists()) return { ...snap.data(), id: snap.id, childId } as CourseDoc;
        return null;
    }

    /** 取得課程列表。childId 為 null 時取得所有孩子的課程（並行請求）
     * @param cachedChildren 若提供則跳過 userSettings 讀取，減少 1 次 Firestore 讀取
     */
    static async getCourses(childId: string | null, cachedChildren?: ChildInfo[]): Promise<CourseDoc[]> {
        const uid = this.getUid();
        if (childId) {
            const colRef = collection(db, 'users', uid, 'children', childId, 'courses');
            const snap = await getDocs(colRef);
            return snap.docs.map(d => Database.mapDocToCourseDoc(d, childId));
        }
        const settings = await this.getUserSettings();
        const children = cachedChildren ?? settings?.children ?? [];
        if (children.length === 0) return [];

        // 並行取得所有孩子的課程，降低總延遲
        const results = await Promise.all(
            children.map(async (child) => {
                const colRef = collection(db, 'users', uid, 'children', child.id, 'courses');
                const snap = await getDocs(colRef);
                return snap.docs.map(d => Database.mapDocToCourseDoc(d, child.id));
            })
        );
        return results.flat();
    }

    /** 將 Firestore 文件轉成 CourseDoc，確保 createdAt 等欄位型別正確（字串或 Timestamp 皆可） */
    private static mapDocToCourseDoc(d: DocumentSnapshot, childId: string): CourseDoc {
        const data = d.data() ?? {};
        const raw = data as Record<string, unknown>;
        const createdAt = raw.createdAt;
        const createdAtStr =
            typeof createdAt === 'string'
                ? createdAt
                : createdAt != null && typeof (createdAt as { toDate?: () => Date }).toDate === 'function'
                    ? (createdAt as { toDate: () => Date }).toDate().toISOString()
                    : undefined;
        return { ...raw, id: d.id, childId, createdAt: createdAtStr } as CourseDoc;
    }

    /** 分頁取得單一孩子的課程，減少大量資料時的讀取次數（需指定 childId）
     * @param pageSize 每頁筆數
     * @param lastDoc 上一頁最後一筆的 snapshot，首次為 undefined
     */
    static async getCoursesPaginated(
        pageSize: number,
        lastDoc: DocumentSnapshot | undefined,
        childId: string
    ): Promise<{ list: CourseDoc[]; lastDoc: DocumentSnapshot | null; hasMore: boolean }> {
        const uid = this.getUid();
        const colRef = collection(db, 'users', uid, 'children', childId, 'courses');
        const q = lastDoc
            ? query(colRef, orderBy('name'), limit(pageSize), startAfter(lastDoc))
            : query(colRef, orderBy('name'), limit(pageSize));
        const snap = await getDocs(q);
        const list = snap.docs.map(d => Database.mapDocToCourseDoc(d, childId));
        const last = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
        return { list, lastDoc: last ?? null, hasMore: snap.docs.length === pageSize };
    }

    static async addCourse(childId: string, data: Omit<CourseDoc, 'id' | 'childId'>): Promise<string> {
        const uid = this.getUid();
        const colRef = collection(db, 'users', uid, 'children', childId, 'courses');
        const createdAt = new Date().toISOString();
        const { createdAt: _drop, ...rest } = data as Omit<CourseDoc, 'id' | 'childId'> & { createdAt?: string };
        const payload = { ...rest, createdAt };
        const docRef = await addDoc(colRef, payload);
        return docRef.id;
    }

    static async updateCourse(childId: string, courseId: string, data: Partial<Omit<CourseDoc, 'id' | 'childId'>>): Promise<void> {
        const uid = this.getUid();
        const docRef = this.docRef(uid, 'children', childId, 'courses', courseId);
        const { id, childId: _c, ...rest } = data as Partial<CourseDoc>;
        const clean = JSON.parse(JSON.stringify(rest)); // 移除 undefined，Firestore 不接受
        try {
            await updateDoc(docRef, clean);
        } catch (e: any) {
            if (e?.code === 'not-found' || e?.code === 5) {
                await setDoc(docRef, clean, { merge: true });
            } else {
                throw e;
            }
        }
    }

    static async deleteCourse(childId: string, courseId: string): Promise<void> {
        const uid = this.getUid();
        const docRef = this.docRef(uid, 'children', childId, 'courses', courseId);
        await deleteDoc(docRef);
    }
}
